import { listen, qs, qsa } from "@fluorescent/dom";
import { slideDown, slideUp, slideStop } from "slide-anim";

import { updateFilters, filterChanged } from "@/lib/filters/events";
import priceRange from "@/lib/filters/price-range";
import FilterSearch from "@/lib/filters/filter-search";
import updateInnerHTML from "@/lib/update-inner-html";
import { on } from "@/glow/events";
import debounce from "@/utils/debounce";

const selectors = {
  scopedForm: formType => `[data-filter-form][data-form-type="${formType}"]`,
  topbarHiddenFilters: "[data-topbar-hidden-filters]",
  filterForm: "[data-filter-form]",
  filterItem: "[data-filter-item]",
  filterList: ".filter-form__list-wrapper",
  groupContents: ".filter-form__group-filter-wrapper",
  filterItemInput: "[data-filter-item-input]",
  group: "[data-filter-group]",
  groupToggle: "[data-filter-group-toggle]",
  drawerBackGroupToggle: "[data-filter-group-toggle-drawer-back]",
  groupToggleByValue: value => `[data-filter-group-toggle="${value}"]`,
  clearGroup: "[data-filter-group-clear]",
  priceRange: "[data-price-range]",
  rangeInput: "[data-range-input]",
  rangeInputMin: '[data-range-input-type="min"]',
  rangeInputMax: '[data-range-input-type="max"]',
  clearAll: "[data-clear-all-filters]",
  filterTruncateToggle: "[data-filter-truncate-toggle]",
  filterTruncateToggleText: "[data-truncate-toggle-text]",
  shouldTruncate: '[data-filter-item-should-truncate="true"]',
};

/**
 * FilterForm
 * @param {Object} node the filter form
 * @returns {Function} unload
 * @returns {Function} renderFilters
 */
const FilterForm = node => {
  if (!node) return false;

  const { formType } = node.dataset;
  let clearAllEls,
    clearGroupEls,
    filterSearch,
    groupEls,
    groupToggleEls,
    rangeContainer,
    rangeInputs;
  let range = null;
  let events = [];

  init();

  function init() {
    groupEls = qsa(selectors.group, node);
    groupToggleEls = qsa(selectors.groupToggle, node);
    clearGroupEls = qsa(selectors.clearGroup, node);
    clearAllEls = qsa(selectors.clearAll, node);
    rangeInputs = qsa(selectors.rangeInput, node);
    rangeContainer = qs(selectors.priceRange, node);
    filterSearch = FilterSearch(node);

    if (rangeContainer) {
      range = priceRange(rangeContainer);
    }

    events.push(
      listen(node, "keydown", handleSubmit),
      listen(node, "change", changeHandler),
      listen(groupToggleEls, "click", toggleGroup),
      listen(rangeInputs, "change", rangeChanged),
      listen(clearGroupEls, "click", clearGroup),
      listen(clearAllEls, "click", clearAllFilters),
      listen(groupEls, "click", toggleGroupTruncate),
      on(["filters:render-filters", "filters:filter-removed"], ({ doc }) =>
        renderFilters(doc)
      )
    );
  }

  function handleSubmit(evt) {
    // Pressing "Enter" on a price range input submits the form, but also causes
    // a "change" event, which leads to a double load. Instead, we prevent "Enter"
    // from applying, and blur the input to trigger the "change".
    if (evt.code == "Enter" && evt.target.hasAttribute("data-range-input")) {
      evt.preventDefault();
      evt.target.blur();
    }
  }

  function changeHandler(evt) {
    if (evt.target.classList.contains("filter-item__checkbox")) {
      filterChange(evt.target);
    } else if (evt.target.classList.contains("filter-item__radio")) {
      sortChange(evt);
    }
  }

  function filterChange(filter) {
    if (filter.dataset.filterItemContentDisabled === "true") {
      return;
    }

    checkForActiveFilters(filter);

    range && range.validateRange();

    // Emit a filter changed event to apply loading styles
    filterChanged();

    let shouldDebounce = true;

    if (filter.id.includes("filter.v.availability")) {
      // HACK: avoid debounce on the in-stock toggle.  Later we should see about
      // a better way to decide what changes get debounced vs not, it is def
      // necessary to debounce on multi select like color or size
      shouldDebounce = false;
    }

    // Debounce the actual filter update event to allow for multiple filter changes in quick succsession
    if (shouldDebounce) {
      debounce(() => updateFilters(node), 1000)();
    } else {
      updateFilters(node);
    }
  }

  function sortChange(e) {
    checkForActiveFilters(e.target);
    range && range.validateRange();
    updateFilters(node);
  }

  function rangeChanged(e) {
    const input = e.currentTarget;

    updateRangeInputs(input);
    checkForActiveFilters(input);
    range && range.validateRange();
    updateFilters(node);
  }

  function updateRangeInputs(input) {
    const type = input.getAttribute("data-range-input-type");

    const oppositeBoundSelectorMap = {
      "min": "rangeInputMax",
      "max": "rangeInputMin",
    };

    const oppositeBoundAttributeMap = {
      "min": "max",
      "max": "min",
    };

    const priceRange = input.closest(selectors.priceRange);

    const oppositeBound = qs(
      selectors[oppositeBoundSelectorMap[type]],
      priceRange
    );

    if (input.value === input.getAttribute(type)) {
      if (
        oppositeBound.value ===
        oppositeBound.getAttribute(oppositeBoundAttributeMap[type])
      ) {
        input.value = "";
        oppositeBound.value = "";
      }
    }
  }

  function toggleGroup(evt) {
    evt.preventDefault();
    const group = qs(`#${evt.currentTarget.getAttribute("aria-controls")}`);
    const ariaExpanded =
      evt.currentTarget.getAttribute("aria-expanded") === "true";

    slideStop(group);

    if (ariaExpanded) {
      closeGroup(evt.currentTarget, group);
    } else {
      openGroup(evt.currentTarget, group);
    }
  }

  function openGroup(button, group) {
    if (formType === "drawer") {
      qsa(selectors.groupToggle, group).forEach(toggle => {
        toggle.setAttribute("aria-expanded", true);
      });
    } else {
      slideDown(group);
      button.setAttribute("aria-expanded", true);
    }

    group.setAttribute("aria-hidden", false);
  }

  function closeGroup(button, group) {
    if (formType === "drawer") {
      qsa(selectors.groupToggle, group).forEach(toggle => {
        toggle.setAttribute("aria-expanded", true);
      });
    } else {
      slideUp(group);
      button.setAttribute("aria-expanded", false);
    }

    group.setAttribute("aria-hidden", true);
  }

  function clearGroup(evt) {
    evt.preventDefault();
    const group = evt.currentTarget.closest(selectors.group);

    if (group) {
      qsa(selectors.filterItemInput, group).forEach(input => {
        if (input.type === "checkbox" || input.type === "radio") {
          input.checked = false;
        } else {
          input.value = "";
          range?.reset();
        }
      });

      updateFilters(node);
      checkGroupActiveFilters(group);
    }
  }

  function clearAllFilters(emitUpdate = true) {
    range?.reset();

    if (!range) {
      rangeInputs.forEach(input => {
        input.value = "";
      });
    }

    qsa(`${selectors.filterItemInput}`, node).forEach(input => {
      if (input.type === "checkbox" || input.type === "radio") {
        input.checked = false;
      }
    });

    if (emitUpdate) {
      updateFilters(node);
    }

    groupEls.forEach(checkGroupActiveFilters);
  }

  function toggleGroupTruncate(evt) {
    const truncateToggleEl = evt.target.closest(selectors.filterTruncateToggle);
    const groupEl = evt.target.closest(selectors.group);

    if (truncateToggleEl && groupEl) {
      const shouldTruncateEls = qsa(selectors.shouldTruncate, groupEl);

      shouldTruncateEls.forEach(element => {
        element.dataset.filterItemIsTruncated =
          element.dataset.filterItemIsTruncated === "true" ? "false" : "true";
      });

      truncateToggleEl.setAttribute(
        "aria-expanded",
        truncateToggleEl.getAttribute("aria-expanded") === "true"
          ? "false"
          : "true"
      );

      const truncateToggleTextEl = qs(
        selectors.filterTruncateToggleText,
        truncateToggleEl
      );

      const buttonLabelAttr =
        truncateToggleEl.getAttribute("aria-expanded") === "true"
          ? "data-show-more-text"
          : "data-show-less-text";

      truncateToggleTextEl.innerHTML =
        truncateToggleEl.getAttribute(buttonLabelAttr);
    }
  }

  function containsCheckedInputs(items) {
    return items.some(input => input.checked);
  }

  function rangeInputsHaveValue() {
    return rangeInputs.some(input => {
      let inputHasValue = input.value !== "";

      if (inputHasValue) {
        const type = input.getAttribute("data-range-inut-type");

        inputHasValue = input.value !== input.getAttribute(type);
      }

      return inputHasValue;
    });
  }

  function checkGroupActiveFilters(group) {
    if (group) {
      const { filterType } = group.dataset;

      if (filterType === "list" || filterType === "boolean") {
        group.dataset.filterGroupHasActiveFilters = containsCheckedInputs(
          qsa("input", group)
        );
      } else if (filterType === "price_range") {
        group.dataset.filterGroupHasActiveFilters = rangeInputsHaveValue();
      }
    }
  }

  function checkForActiveFilters(filterItem) {
    const group = filterItem.closest(selectors.group);
    const activeItems =
      containsCheckedInputs(qsa("input", node)) || rangeInputsHaveValue();

    checkGroupActiveFilters(group);
    node.dataset.filtersActive = activeItems;
  }

  function renderFilters(doc) {
    // The top bar handles updating its own visible content, but we need to update the hidden form.
    // Otherwise, we update the full wrapping container of the filter form.
    if (formType === "topbar") {
      updateInnerHTML(selectors.topbarHiddenFilters, doc);
    } else {
      const updatedForm = qs(selectors.scopedForm(formType), doc);
      const groupToggleSelector = `${selectors.groupToggle}:not(${selectors.drawerBackGroupToggle})`;
      const updatedGroupToggles = qsa(groupToggleSelector, updatedForm);

      updateToggles(node, updatedGroupToggles, groupToggleSelector);

      if (formType === "drawer") {
        const updatedDrawerBackGroupToggles = qsa(
          selectors.drawerBackGroupToggle,
          updatedForm
        );

        updateToggles(
          node,
          updatedDrawerBackGroupToggles,
          selectors.drawerBackGroupToggle
        );
      }

      unload();
      node.setAttribute(
        "data-filters-active",
        updatedForm.getAttribute("data-filters-active")
      );
      node.innerHTML = updatedForm.innerHTML;
      init();
    }
  }

  function updateToggles(
    currentForm,
    updatedGroupToggles,
    groupToggleSelector
  ) {
    updatedGroupToggles.forEach(updatedGroupToggle => {
      const toggleByValueSelector = selectors.groupToggleByValue(
        updatedGroupToggle.getAttribute("data-filter-group-toggle"),
        currentForm
      );
      const currentGroupToggle = qs(
        `${selectors.scopedForm(
          formType
        )} ${groupToggleSelector}${toggleByValueSelector}`,
        currentForm
      );

      if (currentGroupToggle) {
        const currentGroupContents = qs(
          selectors.groupContents,
          currentGroupToggle.closest(selectors.group)
        );
        const updatedGroupContents = qs(
          selectors.groupContents,
          updatedGroupToggle.closest(selectors.group)
        );

        // In the drawer, the main toggle element doesn't get it's 'aria-expanded' attribute updated because there's a
        // "back" button with the drawer pane that does. With that, we only update if we're in a sidebar, or dealing
        // with that "back" button.
        if (
          formType === "sidebar" ||
          groupToggleSelector === selectors.drawerBackGroupToggle
        ) {
          updatedGroupToggle.setAttribute(
            "aria-expanded",
            currentGroupToggle.getAttribute("aria-expanded")
          );
        }

        updatedGroupContents.setAttribute(
          "aria-hidden",
          currentGroupContents.getAttribute("aria-hidden")
        );
      }
    });
  }

  function unload() {
    events.forEach(unsubscribe => unsubscribe());
    events = [];
    range?.unload();
    filterSearch?.unload();
  }

  return {
    unload,
    renderFilters,
    clearAllFilters,
  };
};

export default FilterForm;
